// =============================================================================
// <usp-marquee> — seamless infinite USP marquee (brand layer).
//
// The section (sections/usp-marquee.liquid) renders the merchant blocks ONCE into a single
// `.usp-marquee__group` (platform rule: exactly one `content_for "blocks"` per section). This
// element clones that group — aria-hidden, id-stripped, editor-hooks-stripped — enough times
// that the track is at least 2x the band width, ALWAYS keeping an EVEN number of groups so the
// CSS `translateX(0 → -50%)` loop is pixel-seamless (at -50% an identical group sits exactly
// where group 0 began). Re-tiles on resize / content (image) load via ResizeObserver, and re-runs
// automatically on theme-editor `shopify:section:load` because that replaces the section wrapper,
// re-creating this element and firing connectedCallback().
//
// WCAG 2.2.2 pause-on-hover/focus + prefers-reduced-motion are handled in CSS
// (_usp-marquee.scss). Under reduced motion this element renders NO clones (single static row).
// Dependency-free; registered via the webpack bundle (entry/global.js → assets/global.autojs.js).
// =============================================================================

const TAG = 'usp-marquee';
const CLONE_CLASS = 'usp-marquee__group--clone';

class UspMarquee extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('.usp-marquee__track');
    this.group = this.querySelector('.usp-marquee__group');
    if (!this.track || !this.group) return;

    this.render = this.render.bind(this);
    this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    if ('ResizeObserver' in window) {
      // Observe the host (viewport / responsive .h3 font changes) AND the group
      // (its width grows when the icon images finish loading) so we re-tile on both.
      this.observer = new ResizeObserver(this.render);
      this.observer.observe(this);
      this.observer.observe(this.group);
    } else {
      window.addEventListener('resize', this.render);
      this.render();
    }

    if (this.reduceMotion.addEventListener) {
      this.reduceMotion.addEventListener('change', this.render);
    }
  }

  disconnectedCallback() {
    if (this.observer) this.observer.disconnect();
    window.removeEventListener('resize', this.render);
    if (this.reduceMotion && this.reduceMotion.removeEventListener) {
      this.reduceMotion.removeEventListener('change', this.render);
    }
  }

  render() {
    // Always clear previous clones first so re-runs are idempotent.
    const existing = this.track.querySelectorAll('.' + CLONE_CLASS);
    existing.forEach((node) => node.remove());

    // Reduced motion: keep the single authored group, no clones (CSS centres + freezes it).
    if (this.reduceMotion && this.reduceMotion.matches) return;

    const bandWidth = this.clientWidth;
    const groupWidth = this.group.getBoundingClientRect().width;
    if (!bandWidth || !groupWidth) return;

    // Half the track must cover the band; total stays EVEN so -50% lands seamlessly.
    const half = Math.max(1, Math.ceil(bandWidth / groupWidth));
    const total = half * 2;

    const fragment = document.createDocumentFragment();
    for (let i = 1; i < total; i += 1) {
      const clone = this.group.cloneNode(true);
      clone.classList.add(CLONE_CLASS);
      clone.setAttribute('aria-hidden', 'true');
      stripEditorHooks(clone);
      const descendants = clone.querySelectorAll('*');
      descendants.forEach(stripEditorHooks);
      fragment.appendChild(clone);
    }
    this.track.appendChild(fragment);
  }
}

// Remove the id + theme-editor data hooks from a cloned node so the editor only ever
// targets the real, authored blocks (and ids stay unique).
function stripEditorHooks(el) {
  if (!el.removeAttribute) return;
  el.removeAttribute('id');
  for (let i = el.attributes.length - 1; i >= 0; i -= 1) {
    const name = el.attributes[i].name;
    if (name.indexOf('data-shopify') === 0) el.removeAttribute(name);
  }
}

if (window.customElements && !customElements.get(TAG)) {
  customElements.define(TAG, UspMarquee);
}

export default UspMarquee;
